/**
 * Step 4: Related Tasks Component - Redesigned with Panel Pattern
 *
 * Simplified wizard step using a slide panel for task selection.
 * Displays a CTA button when no tasks selected, or a list with an "Add more"
 * button when tasks are selected.
 *
 * The selected-task display data is hydrated independently of the global tasks
 * store and of the picker's pagination/filters (`useSelectedRelatedTasks`), so
 * chips render even when a selected task is not on the current picker page.
 *
 * @module components/tasks/wizard/steps/Step4RelatedTasks
 */

import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';
import { Stack, Button, Box } from '@mantine/core';
import { IconPlus } from '@tabler/icons-react';
import { UseFormReturnType } from '@mantine/form';

import { AppScrollArea } from '@/components/ui/AppScrollArea';
import { useGeoMarkupStore } from '@/store';
import { selectSelectedTask } from '@/store/selectors/taskSelectors';
import { MantineWizardFormData } from '../../utils/mantineFormSchema';
import { FormSection } from '../../../../common/form';
import { SettingsToggleRow } from '../../../../common/SettingsToggleRow';
import { RelatedTasksCTA, SelectedTaskItem } from './components';
import { RelatedTasksListPanel } from './RelatedTasksListPanel';
import { useSelectedRelatedTasks } from './hooks/useSelectedRelatedTasks';
import { ICON_SIZE_SM } from '../../../../../constants/ui';

import styles from './Step4RelatedTasks.module.css';
import sharedStyles from '../../../../common/styles/ScrollContainerLayout.module.css';

interface Step4RelatedTasksProps {
  form: UseFormReturnType<MantineWizardFormData>;
  editingTaskId?: string | null;
}

export const Step4RelatedTasks: React.FC<Step4RelatedTasksProps> = ({
  form,
  editingTaskId,
}) => {
  const { t } = useTranslation();

  const toggleRelatedTasksList = useGeoMarkupStore(
    (state) => state.ui.toggleRelatedTasksList
  );

  // Seed hydration from the edited task's related-task summaries (when editing)
  // to avoid an extra round-trip; the hook still falls back to getById for any
  // id the summaries don't cover.
  const relatedTaskSummaries = useGeoMarkupStore(
    useShallow((state) =>
      editingTaskId ? (selectSelectedTask(state)?.relatedTasks ?? []) : []
    )
  );

  // Independent display data for the selected tasks (decoupled from the global
  // list and from the picker pagination/filters).
  const { selectedTasks, cacheTasks } = useSelectedRelatedTasks({
    relatedTaskIds: form.values.relatedTaskIds,
    relatedTaskSummaries,
  });

  const hasSelectedTasks = selectedTasks.length > 0;

  const handleOpenPanel = useCallback(() => {
    toggleRelatedTasksList(true);
  }, [toggleRelatedTasksList]);

  const handleRemove = useCallback(
    (taskId: string) => {
      const newIds = form.values.relatedTaskIds.filter((id) => id !== taskId);
      form.setFieldValue('relatedTaskIds', newIds);
    },
    [form]
  );

  const handleTasksSelect = useCallback(
    (taskIds: string[]) => {
      form.setFieldValue('relatedTaskIds', taskIds);
    },
    [form]
  );

  return (
    <>
      <FormSection
        title={t('wizard.step4.heading')}
        description={t('wizard.step4.description')}
      >
        <Box className={styles.container}>
          {/* Case 1: No tasks selected - show CTA */}
          {!hasSelectedTasks && <RelatedTasksCTA onClick={handleOpenPanel} />}

          {/* Case 2: Tasks selected - scrollable container */}
          {hasSelectedTasks && (
            <Box className={sharedStyles.scrollContainer}>
              {/* Header - "Add more" button */}
              <Box
                className={`${sharedStyles.header} ${styles.headerWithBorder}`}
              >
                <Button
                  variant="light"
                  leftSection={<IconPlus size={ICON_SIZE_SM} />}
                  onClick={handleOpenPanel}
                >
                  {t('wizard.step4.addMore')}
                </Button>
              </Box>

              {/* Scrollable Content - Selected tasks list */}
              <AppScrollArea fill>
                <Stack gap="xs" className={styles.selectedTasksList}>
                  {selectedTasks.map((task) => (
                    <SelectedTaskItem
                      key={task.id}
                      task={task}
                      onRemove={handleRemove}
                      variant="light"
                    />
                  ))}
                </Stack>
              </AppScrollArea>
            </Box>
          )}

          {/* Toggle - always visible when tasks selected */}
          {hasSelectedTasks && (
            <Box className={styles.requireToggleWrapper}>
              <SettingsToggleRow
                label={t('wizard.step4.requireCompletion')}
                description={t('wizard.step4.requireCompletionDesc')}
                checked={form.values.requireRelatedTasksCompletion}
                onChange={(checked) =>
                  form.setFieldValue('requireRelatedTasksCompletion', checked)
                }
              />
            </Box>
          )}
        </Box>
      </FormSection>

      {/* Related Tasks List Panel */}
      <RelatedTasksListPanel
        selectedTaskIds={form.values.relatedTaskIds}
        selectedTasks={selectedTasks}
        onTasksSelect={handleTasksSelect}
        onCacheTasks={cacheTasks}
        editingTaskId={editingTaskId}
      />
    </>
  );
};
